import { Button, MenuItem, Select, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiDownload,
  FiEye,
  FiFileText,
  FiPlus,
  FiRefreshCw,
  FiSave
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";

import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import ConfirmDialog from "../../components/ConfirmDialog";
import {
  TechnicalReportDeviceInterface,
  TechnicalReportInterface,
  WorkOrderDeviceJoinInterface
} from "../../common/interfaces";
import { TechnicalReportStatus } from "../../components/enum/technicalReportEnums";
import PageToaster from "../../components/toaster/PageToaster";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  CenterMessage,
  Container,
  Grid,
  HeaderBar,
  HeaderLeft,
  HeaderRight,
  StatusChip,
  Title,
  TitleBlock
} from "../workOrder/workorder-detail.styled";
import {
  addDeviceToReport,
  completeReport,
  createOrGetDraft,
  downloadTechnicalReportPdf,
  fetchWorkOrderDevices,
  listReportDevices,
  previewTechnicalReportPdf,
  reopenReport,
  updateReport
} from "./actions";
import TechnicalReportDeviceBlock from "./TechnicalReportDeviceBlock";
import SignaturesPanel from "../documents/SignaturesPanel";
import { FieldsGrid } from "./technical-report.styled";

const STATUS_COLOR: Record<string, string> = {
  DRAFT: "#f59e0b",
  COMPLETED: "#22c55e",
  REOPENED: "#3b82f6"
};

const toDatetimeLocal = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromDatetimeLocal = (value: string): string | undefined => {
  if (!value) return undefined;
  return new Date(value).toISOString();
};

const TechnicalReportPage: React.FC = () => {
  const { id: workOrderId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isUser } = useAuth();

  const containerId = "toaster-technical-report";
  const { autoSuccess, autoError } = usePageToast(containerId, true);
  const handleHttpError = makeToasterErrorHandler(autoError);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [report, setReport] = useState<TechnicalReportInterface | null>(null);
  const [reportDevices, setReportDevices] = useState<TechnicalReportDeviceInterface[]>([]);
  const [workOrderDevices, setWorkOrderDevices] = useState<WorkOrderDeviceJoinInterface[]>([]);
  const [deviceToAdd, setDeviceToAdd] = useState("");
  const [addingDevice, setAddingDevice] = useState(false);
  const [confirmReopenOpen, setConfirmReopenOpen] = useState(false);

  const [executionLocation, setExecutionLocation] = useState("");
  const [unitCode, setUnitCode] = useState("");
  const [unitName, setUnitName] = useState("");
  const [technicalResponsibleUserId, setTechnicalResponsibleUserId] = useState("");
  const [executionStartedAt, setExecutionStartedAt] = useState("");
  const [checkInAt, setCheckInAt] = useState("");
  const [checkOutAt, setCheckOutAt] = useState("");
  const [criticality, setCriticality] = useState("");
  const [servicesSummary, setServicesSummary] = useState("");
  const [technicalConclusion, setTechnicalConclusion] = useState("");

  const hydrateForm = (rep: TechnicalReportInterface) => {
    setExecutionLocation(rep.execution_location ?? "");
    setUnitCode(rep.unit_code ?? "");
    setUnitName(rep.unit_name ?? "");
    setTechnicalResponsibleUserId(
      rep.technical_responsible_user_id ? String(rep.technical_responsible_user_id) : ""
    );
    setExecutionStartedAt(toDatetimeLocal(rep.execution_started_at));
    setCheckInAt(toDatetimeLocal(rep.check_in_at));
    setCheckOutAt(toDatetimeLocal(rep.check_out_at));
    setCriticality(rep.criticality ?? "");
    setServicesSummary(rep.services_summary ?? "");
    setTechnicalConclusion(rep.technical_conclusion ?? "");
  };

  useEffect(() => {
    if (!workOrderId) return;
    (async () => {
      try {
        setLoading(true);
        const rep = await createOrGetDraft(workOrderId);
        setReport(rep);
        hydrateForm(rep);
        const [devicesInReport, allDevices] = await Promise.all([
          listReportDevices(rep.technical_report_id),
          fetchWorkOrderDevices(workOrderId)
        ]);
        setReportDevices(devicesInReport);
        setWorkOrderDevices(allDevices);
      } catch (err) {
        handleHttpError(err);
      } finally {
        setLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    })();
  }, [workOrderId]);

  const locked = report?.status === "COMPLETED";

  const availableDevices = workOrderDevices.filter(
    (d) => !reportDevices.some((rd) => rd.work_order_device_id === d.id)
  );

  const buildPayload = () => ({
    execution_location: executionLocation || undefined,
    unit_code: unitCode || undefined,
    unit_name: unitName || undefined,
    technical_responsible_user_id: technicalResponsibleUserId
      ? Number(technicalResponsibleUserId)
      : undefined,
    execution_started_at: fromDatetimeLocal(executionStartedAt),
    check_in_at: fromDatetimeLocal(checkInAt),
    check_out_at: fromDatetimeLocal(checkOutAt),
    criticality: criticality || undefined,
    services_summary: servicesSummary || undefined,
    technical_conclusion: technicalConclusion || undefined
  });

  const handleSave = async () => {
    if (!report || !workOrderId) return;
    try {
      setSaving(true);
      const updated = await updateReport(workOrderId, buildPayload());
      setReport(updated);
      autoSuccess("Rascunho salvo.");
    } catch (err) {
      handleHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (!report || !workOrderId) return;
    if (!technicalConclusion.trim() && !report.technical_conclusion) {
      autoError("Preencha a conclusão técnica antes de concluir.");
      return;
    }
    if (reportDevices.length === 0) {
      autoError("Adicione pelo menos um equipamento ao relatório antes de concluir.");
      return;
    }
    try {
      setSaving(true);
      const updated = await completeReport(workOrderId, {
        check_out_at: fromDatetimeLocal(checkOutAt),
        services_summary: servicesSummary || undefined,
        technical_conclusion: technicalConclusion || undefined
      });
      setReport(updated);
      autoSuccess("Relatório concluído.");
    } catch (err) {
      handleHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleReopen = async () => {
    if (!report) return;
    try {
      setSaving(true);
      const updated = await reopenReport(report.technical_report_id);
      setReport(updated);
      autoSuccess("Relatório reaberto.");
    } catch (err) {
      handleHttpError(err);
    } finally {
      setSaving(false);
      setConfirmReopenOpen(false);
    }
  };

  const handleAddDevice = async () => {
    if (!report || !deviceToAdd) return;
    try {
      setAddingDevice(true);
      await addDeviceToReport(report.technical_report_id, Number(deviceToAdd));
      const fresh = await listReportDevices(report.technical_report_id);
      setReportDevices(fresh);
      setDeviceToAdd("");
      autoSuccess("Equipamento adicionado ao relatório.");
    } catch (err) {
      handleHttpError(err);
    } finally {
      setAddingDevice(false);
    }
  };

  const handleDeviceRemoved = (workOrderDeviceId: number) => {
    setReportDevices((prev) => prev.filter((d) => d.work_order_device_id !== workOrderDeviceId));
  };

  if (loading) return <CenterMessage>Carregando...</CenterMessage>;
  if (!report) return <CenterMessage>Relatório não encontrado.</CenterMessage>;

  return (
    <Container>
      <PageToaster containerId={containerId} />

      <HeaderBar>
        <HeaderLeft>
          <TitleBlock>
            <span className="eyebrow">Relatório Técnico</span>
            <Title>{report.report_number}</Title>
          </TitleBlock>
          <StatusChip $color={STATUS_COLOR[report.status]}>
            {TechnicalReportStatus[report.status]}
          </StatusChip>
        </HeaderLeft>
        <HeaderRight>
          <Button
            variant="outlined"
            startIcon={<FiArrowLeft />}
            onClick={() => navigate(`/workorder-details/${workOrderId}`)}
          >
            Voltar
          </Button>
        </HeaderRight>
      </HeaderBar>

      <Card>
        <CardHeader>
          <CardTitle>
            <FiFileText /> Atendimento
          </CardTitle>
        </CardHeader>
        <CardBody>
          <FieldsGrid>
            <TextField size="small" label="Local de execução" value={executionLocation} disabled={locked}
              onChange={(e) => setExecutionLocation(e.target.value)} />
            <TextField size="small" label="Código da unidade/loja" value={unitCode} disabled={locked}
              onChange={(e) => setUnitCode(e.target.value)} />
            <TextField size="small" label="Nome da unidade/loja" value={unitName} disabled={locked}
              onChange={(e) => setUnitName(e.target.value)} />
            <TextField size="small" type="number" label="ID do responsável técnico" value={technicalResponsibleUserId}
              disabled={locked} onChange={(e) => setTechnicalResponsibleUserId(e.target.value)} />
            <TextField size="small" type="datetime-local" label="Início da execução" InputLabelProps={{ shrink: true }}
              value={executionStartedAt} disabled={locked} onChange={(e) => setExecutionStartedAt(e.target.value)} />
            <TextField size="small" type="datetime-local" label="Check-in" InputLabelProps={{ shrink: true }}
              value={checkInAt} disabled={locked} onChange={(e) => setCheckInAt(e.target.value)} />
            <TextField size="small" type="datetime-local" label="Check-out" InputLabelProps={{ shrink: true }}
              value={checkOutAt} disabled={locked} onChange={(e) => setCheckOutAt(e.target.value)} />
            <TextField size="small" label="Criticidade" value={criticality} disabled={locked}
              onChange={(e) => setCriticality(e.target.value)} />
          </FieldsGrid>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <FiCheckCircle /> Serviços e conclusão
          </CardTitle>
        </CardHeader>
        <CardBody>
          <FieldsGrid>
            <TextField fullWidth multiline minRows={3} label="Serviços realizados" value={servicesSummary}
              disabled={locked} onChange={(e) => setServicesSummary(e.target.value)} />
            <TextField fullWidth multiline minRows={3} label="Conclusão técnica" value={technicalConclusion}
              disabled={locked} onChange={(e) => setTechnicalConclusion(e.target.value)} />
          </FieldsGrid>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Equipamentos no relatório</CardTitle>
        </CardHeader>
        <CardBody>
          {!locked && (
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
              <Select
                size="small"
                displayEmpty
                value={deviceToAdd}
                onChange={(e) => setDeviceToAdd(e.target.value as string)}
                style={{ minWidth: 260 }}
              >
                <MenuItem value="">
                  {availableDevices.length ? "Selecione um equipamento..." : "Todos os equipamentos já foram adicionados"}
                </MenuItem>
                {availableDevices.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.device?.device_name} {d.device?.device_serial_number ? `(${d.device.device_serial_number})` : ""}
                  </MenuItem>
                ))}
              </Select>
              <Button
                variant="contained"
                startIcon={<FiPlus />}
                disabled={!deviceToAdd || addingDevice}
                onClick={handleAddDevice}
              >
                Adicionar
              </Button>
            </div>
          )}

          {reportDevices.length === 0 ? (
            <p style={{ color: "var(--text-muted)" }}>
              Nenhum equipamento incluído ainda. Selecione um ou mais equipamentos da OS acima.
            </p>
          ) : (
            <Grid style={{ gridTemplateColumns: "1fr", gap: 16 }}>
              {reportDevices.map((deviceRow) => (
                <TechnicalReportDeviceBlock
                  key={deviceRow.technical_report_device_id}
                  reportId={report.technical_report_id}
                  deviceRow={deviceRow}
                  locked={locked}
                  autoSuccess={autoSuccess}
                  autoError={autoError}
                  onRemoved={() => handleDeviceRemoved(deviceRow.work_order_device_id)}
                />
              ))}
            </Grid>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <SignaturesPanel
            reportId={report.technical_report_id}
            isLocked={locked}
            canDelete={!isUser}
            onNotify={autoSuccess}
            onError={handleHttpError}
          />
        </CardBody>
      </Card>

      <Card>
        <CardBody style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "flex-end" }}>
          <Button variant="outlined" startIcon={<FiSave />} disabled={locked || saving} onClick={handleSave}>
            Salvar rascunho
          </Button>
          <Button variant="contained" color="success" startIcon={<FiCheckCircle />} disabled={locked || saving}
            onClick={handleComplete}>
            Concluir
          </Button>
          {report.status === "COMPLETED" && !isUser && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<FiRefreshCw />}
              disabled={saving}
              onClick={() => setConfirmReopenOpen(true)}
            >
              Reabrir
            </Button>
          )}
          <Button variant="outlined" startIcon={<FiEye />} onClick={() => previewTechnicalReportPdf(workOrderId!)}>
            Pré-visualizar PDF
          </Button>
          <Button variant="outlined" startIcon={<FiDownload />} onClick={() => downloadTechnicalReportPdf(workOrderId!)}>
            Baixar PDF
          </Button>
        </CardBody>
      </Card>

      <ConfirmDialog
        open={confirmReopenOpen}
        title="Reabrir relatório?"
        description="O relatório concluído voltará a ficar editável e o PDF deixará de refletir os dados originais até uma nova conclusão. Deseja continuar?"
        confirmText="Reabrir"
        cancelText="Cancelar"
        onClose={() => setConfirmReopenOpen(false)}
        onConfirm={handleReopen}
      />
    </Container>
  );
};

export default TechnicalReportPage;
